import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'modules/ui/form/useForm';
import { Loader, SelectWithLabel } from 'modules/ui';
import { useLeasingCalculatorCodes } from 'modules/hooks/codesHooks/useLeasingCalculatorCodes';
import { FormButtons, FormContainer } from 'modules/ui/form';
import { LeasingCalculatorCodesSelectors } from 'modules/hooks/codesHooks/redux/leasingCalculatorCodes';
import { LeasingCalculatorSharedFormProps } from '../../common/LeasingCalculatorData';
import { LeasingCurrencyEnum } from '../../common/helper';
import { useAppSelector } from '../../../../hooks/appHooks';
import { ValidationType } from '../../../../../types/ValidationType';

export const LeasingCalculatorGeneralData: React.FC<
  LeasingCalculatorSharedFormProps
> = ({ defaultValues, onChange, onNextStep, onPreviousStep }) => {
  const { t } = useTranslation();

  const generalDataCodesLoading = useAppSelector(
    LeasingCalculatorCodesSelectors.selectGeneralDataCodesLoading,
  );

  const {
    leasingRecipients,
    leasingTypes,
    leasingObjectSuppliers,
    leasingCurrencies,
    findLeasingCurrencyByName,
  } = useLeasingCalculatorCodes();

  const { validateFlag, handleOnSubmit, updateFormData, updateSelectData } =
    useForm({
      onChange,
      onNextStep,
      onPreviousStep,
    });

  useEffect(() => {
    if (!defaultValues?.leasingCurrency && leasingCurrencies.options) {
      const currencyEuro = findLeasingCurrencyByName(LeasingCurrencyEnum.EURO);
      currencyEuro && updateFormData('leasingCurrency', currencyEuro);
    }
  }, [
    defaultValues?.leasingCurrency,
    findLeasingCurrencyByName,
    leasingCurrencies.options,
    updateFormData,
  ]);

  useEffect(() => {
    if (!defaultValues?.leasingCurrency && leasingCurrencies.options) {
      const currencyEuroId = findLeasingCurrencyByName(
        LeasingCurrencyEnum.EURO,
      );
      currencyEuroId && updateFormData('leasingCurrency', currencyEuroId);
    }
  }, [
    defaultValues?.leasingCurrency,
    findLeasingCurrencyByName,
    leasingCurrencies.options,
    updateFormData,
  ]);

  return (
    <Loader active={generalDataCodesLoading}>
      <FormContainer onSubmit={handleOnSubmit}>
        <SelectWithLabel
          required
          searchable={false}
          label={t('leasingCalculator.generalData.leasingRecipientType')}
          options={leasingRecipients.options}
          placeholderText={t(
            'leasingCalculator.generalData.leasingRecipientTypePlaceholder',
          )}
          defaultValue={defaultValues?.leasingRecipientType?.id}
          onChange={(value) =>
            updateSelectData(
              'leasingRecipientType',
              value,
              leasingRecipients.uwtTypes,
            )
          }
          validators={[ValidationType.REQUIRED]}
          validate={validateFlag}
        />

        <SelectWithLabel
          required
          searchable={false}
          label={t('leasingCalculator.generalData.leasingType')}
          options={leasingTypes.options}
          placeholderText={t(
            'leasingCalculator.generalData.leasingTypePlaceholder',
          )}
          defaultValue={defaultValues?.leasingType?.id}
          onChange={(value) =>
            updateSelectData('leasingType', value, leasingTypes.uwtTypes)
          }
          validators={[ValidationType.REQUIRED]}
          validate={validateFlag}
        />

        <SelectWithLabel
          required
          searchable={false}
          label={t('leasingCalculator.generalData.leasingObjectSupplier')}
          options={leasingObjectSuppliers.options}
          placeholderText={t(
            'leasingCalculator.generalData.leasingObjectSupplierPlaceholder',
          )}
          defaultValue={defaultValues?.leasingObjectSupplier?.id}
          onChange={(value) =>
            updateSelectData(
              'leasingObjectSupplier',
              value,
              leasingObjectSuppliers.uwtTypes,
            )
          }
          validators={[ValidationType.REQUIRED]}
          validate={validateFlag}
        />

        <SelectWithLabel
          required
          searchable={false}
          label={t('leasingCalculator.generalData.currency')}
          options={leasingCurrencies.options}
          defaultValue={defaultValues?.leasingCurrency?.id}
          onChange={(value) =>
            updateSelectData(
              'leasingCurrency',
              value,
              leasingCurrencies.uwtTypes,
            )
          }
          validators={[ValidationType.REQUIRED]}
          validate={validateFlag}
          readOnly
        />
        <FormButtons onPreviousStep={onPreviousStep} />
      </FormContainer>
    </Loader>
  );
};
